import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Container, Row, Col, Card, Form, Button } from "react-bootstrap";
import CustomContentAlert, { navigationButtons, removeSpecialChar, validation_message } from "../helper";

import { getQuickPermitSetupDetails, createQuickPermitSetup } from "../../Actions/CurrentVisitorPermitActions/actions";
import NoteSectionInSetting from "./SettingComponents/NoteSectionInSetting";
import IdFieldSectionSetting from "./SettingComponents/IdFieldSectionSetting";
import RegisterOnTerminalSectionInSetting from "./SettingComponents/RegisterOnTerminalSectionInSetting";
import TitleSectionInSetting from "./SettingComponents/TitleSectionInSetting";
import { getQuickPermitSetupDetailForProperty } from "../../Selectors/CurrentVisitorPermitSelector";
import { getAPIResponse } from "../../Selectors/APIResponseReducer";
import { useLocation } from "react-router";
import useGetAllProperty from "../../Hooks/Property/useGetAllProperty";
import { validateIdFields, validateTitleField, validateNoteField, validateNoteOptions, getOptions } from "./QuickPermitComman";
import { SuperAdminAction } from "../../Actions"; // Add import for SuperAdminAction

const initialFormData = {
  quick_permit_form_title: "",
  instruction: "",
  name_of_note: "",
  name_of_id: "",
  mincharacter: 2,
  maxcharacter: 10,
  parking_message: "",
  id_validation: "",
  register_on_terminal: "",
  self_server: false,
  regular: false,
  show_id: false,
  note_options: [],
  lot_id: "",
};

const intialError = {
  quick_permit_form_title: "",
  mincharacter: "",
  maxcharacter: "",
  name_of_id: "",
  name_of_note: "",
  note_options: "",
  parking_message: "",
  lot_id: "",
};

const QuickPermitSetup = (_props) => {
  const [formData, setFormData] = useState({ ...initialFormData });
  const [errorState, setErrorState] = useState({ ...intialError });
  const [isFormValid, setIsFormValid] = useState(true);
  const [isUpdate, setIsUpdate] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [showError, setShowError] = useState(false);

  const [ticketReportLots, setTicketReportLots] = useState([]);
  const QuickPermitSetupDetail = useSelector(getQuickPermitSetupDetailForProperty);
  const { api_response, api_error_message, api_success_message } = useSelector(getAPIResponse);

  const dispatch = useDispatch();
  const location = useLocation();
  const { propertyId } = useGetAllProperty(location.search?.slice(1));
  const lots = useSelector((state) => state.SuperAdminDetails.lots || []);

  useEffect(() => {
    dispatch(getQuickPermitSetupDetails(propertyId));
    dispatch(SuperAdminAction.get_all_lots(propertyId));
    return () => {
      dispatch({ type: "FETCH_QUICK_PERMIT_SETTING_DETAILS_BLANK" });
    };
  }, []);

  useEffect(() => {
    if (api_response) {
      if (QuickPermitSetupDetail && Object.keys(QuickPermitSetupDetail).length) {
        setIsUpdate(true);
        setFormData({
          ...formData,
          quick_permit_form_title: QuickPermitSetupDetail?.quick_permit_form_title,
          instruction: QuickPermitSetupDetail?.instruction,
          name_of_note: QuickPermitSetupDetail?.name_of_note,
          name_of_id: QuickPermitSetupDetail?.name_of_id,
          self_server: QuickPermitSetupDetail?.self_server,
          regular: QuickPermitSetupDetail?.regular,
          show_id: QuickPermitSetupDetail?.show_id,
          parking_message: QuickPermitSetupDetail?.parking_message,
          register_on_terminal: QuickPermitSetupDetail?.register_on_terminal,
          id_validation: QuickPermitSetupDetail?.id_validation,
          note_options: getOptions(QuickPermitSetupDetail?.note_options),
          mincharacter: QuickPermitSetupDetail?.id_min_char,
          maxcharacter: QuickPermitSetupDetail?.id_max_char,
          lot_id: QuickPermitSetupDetail?.lot_id || formData.lot_id, // Fallback to current selection
        });
      } else {
        setIsUpdate(false);
      }
    }
  }, [api_response, QuickPermitSetupDetail]);
  
  const currentLot = useMemo(() => {
    if (!formData.lot_id || !lots?.length) return null;
    const matched = lots.find(l => String(l.id) === String(formData.lot_id));
    return matched ? (matched.alias || matched.lotName || `Lot ${matched.id}`) : null;
  }, [formData.lot_id, lots]);
  // 1. THE ONE AND ONLY pure Admin Quick Permit lot: quick_permits=1 AND plate_validate=0
  const adminQuickPermitLot = useMemo(() => {
    const candidates = lots.filter(lot => lot.quick_permits && !lot.plate_validate);
    return candidates.length > 0 ? candidates[0] : null;
  }, [lots]);

  // 2. All lots that can be used for Self-Registration: any lot with quick_permits = 1
  const selfRegistrationLots = useMemo(() => {
    return lots.filter(lot => lot.quick_permits);
  }, [lots]);


  const handleValidation = (obj) => {
    let errObj = { ...errorState };
    errObj = validateTitleField(obj, errObj);
    errObj = validateNoteField(obj, errObj);
    errObj = validateIdFields(obj, errObj);
    errObj = validateNoteOptions(obj, errObj);
    // Validate lot selection only if self_server is true and selectedLotId is empty
    if (obj.self_server && !obj.lot_id) {
      errObj.lot_id = "Please select a lot for self-registration";
    } else {
      errObj.lot_id = ""; // Clear error if a lot is selected or self_server is false
    }

    setErrorState({ ...errObj });
    setIsFormValid(!Object.keys(errObj).find((x) => errObj[x].length !== 0));
    return !Object.keys(errObj).find((x) => errObj[x].length === 0);
  };

  const handleChange = async ({ target }) => {
    const { name, value } = target;
    let newValue = !["instruction", "parking_message", "lot_id"].includes(name) ? removeSpecialChar(value) : value;
    let obj = { ...formData };
    if (["mincharacter", "maxcharacter"].includes(name)) {
      newValue = newValue.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");
    }
    obj = { ...obj, [name]: newValue };
    handleValidation(obj);
    setFormData({
      ...formData,
      ...obj,
    });
  };

  const handleCheckBox = async (e) => {
    const { name, checked } = e.target;
    let obj = { ...formData, [name]: checked };
    if (name === "show_id" && checked === false) {
      obj = {
        ...obj,
        name_of_id: "ID",
        maxcharacter: 8,
        mincharacter: 2,
      };
    }
    // Clear lot selection when self_server is disabled
    if (name === "self_server" && !checked) {
      obj = { ...obj, lot_id: "" };
    }
    setFormData({ ...obj });
    handleValidation(obj);
    // Use existing lots when self_server is enabled
    if (name === "self_server" && checked && lots.length > 0) {
      setTicketReportLots(lots.filter(l => l.quick_permits === true));
      console.log("Using existing lots for self_server:", lots);
    }
  };

  const handleRadioChange = (name, e) => {
    setFormData({
      ...formData,
      [name]: e.target.value,
    });
  };

  const isNotallow = useMemo(
    () => formData.self_server === true || formData.regular === true || formData.self_server === 1 || formData.regular === 1,
    [formData.self_server, formData.regular],
  );

  const handleCreateQuickPermitSetup = async () => {
    const data = {
      title: formData.quick_permit_form_title,
      min_character: formData.mincharacter,
      max_character: formData.maxcharacter,
      allow_notes: isNotallow,
      property_id: propertyId,
      note_options: JSON.stringify(formData.note_options?.filter((x) => x.trim().length !== 0)),
      instruction: formData.instruction,
      name_of_note: formData.name_of_note,
      name_of_id: formData.name_of_id,
      self_server: formData.self_server,
      regular: formData.regular,
      show_id: formData.show_id,
      id_validation: formData.id_validation,
      register_on_terminal: formData.register_on_terminal,
      parking_message: formData.parking_message,
      lot_id: formData.lot_id, // Include selected lot
    };
    const result = dispatch(createQuickPermitSetup(data, isUpdate));
    dispatch(createQuickPermitSetup(data, isUpdate));
    if (api_response) {
      setShowAlert(true);
      setIsUpdate(true);
      setTimeout(() => {
        setShowAlert(false);
      }, 4000);
      // Sync state with server response, handling undefined
      const newSelectedLotId = (result?.payload?.lot_id) ? result.payload.lot_id : data.lot_id; // Fallback to sent value
      setFormData(prev => ({ ...prev, selectedLotId: newSelectedLotId }));
    } else {
      setShowError(true);
      setTimeout(() => {
        setShowError(true);
      }, 4000);
    }
  };

  return (
    <div>
      <Container fluid>
        <Row>
          <Col md="12">
            <Card>
              <Card.Header>
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <Card.Title as="h4">Quick Permit Setup</Card.Title>
                  </div>
                </div>
              </Card.Header>
              <Card.Body className="pos-rel-overflow-hide">
                <Row>
                  <Col lg="12">
                    <Form.Group>
                      <TitleSectionInSetting formData={formData} handleChange={handleChange} errorState={errorState} validation_message={validation_message} />
                      <hr />
                      <NoteSectionInSetting
                        handleCheckBox={handleCheckBox}
                        formData={formData}
                        isNotallow={isNotallow}
                        errorState={errorState}
                        handleChange={handleChange}
                        setFormData={setFormData}
                        handleValidation={handleValidation}
                      />
                      <hr />
                      {/* Lot selection dropdown */}
                      {formData.self_server && (
                        <>
                          {/* ADMIN QUICK PERMIT LOT – PURE BLACK & WHITE */}
                          {adminQuickPermitLot ? (
                            <Form.Group className="mb-4">
                              <Form.Label className="fw-italic">
                                Note: "Quick Permit" page uses Parking Area Or Lot:
                                {" "}
                                <span style={{ fontWeight: "bold" }}>
                                  {adminQuickPermitLot
                                    ? (adminQuickPermitLot.alias ||
                                      adminQuickPermitLot.lotName ||
                                      `Lot ${adminQuickPermitLot.id}`)
                                    : "None Configured"}
                                </span>
                              </Form.Label>
                            </Form.Group>
                          ) : (
                            <div className="border rounded p-3 mb-4 bg-white">
                              <strong>No Pure Quick Permit Lot Configured</strong><br />
                              <small className="text-muted">
                                Required: One lot with Quick Permit = Yes and Plate Validate = No
                              </small>
                            </div>
                          )}
                          {/* SELF-REGISTRATION LOT SELECTION (SIMPLIFIED) */}
                          <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                              Select Parking Area or Lot for Self-Register <span className="text-danger">*</span>
                            </Form.Label>

                            {selfRegistrationLots.length === 0 ? (
                              <div className="border rounded p-3 bg-white">
                                <strong>No lot available for self-registration</strong><br />
                                <small className="text-muted">At least one lot must have Quick Permit = Yes</small>
                              </div>
                            ) : (
                              <Form.Control
                                as="select"
                                name="lot_id"
                                value={formData.lot_id || ""}
                                onChange={handleChange}
                                isInvalid={!!errorState.lot_id}
                                required // optional: enforces selection in HTML5 validation
                              >
                                <option value="" disabled>
                                  Select Parking Area or Lot...
                                </option>
                                {selfRegistrationLots.map(lot => (
                                  <option key={lot.id} value={lot.id}>
                                    {lot.alias || lot.lotName || `Lot ${lot.id}`}
                                  </option>
                                ))}
                              </Form.Control>
                            )}

                            <Form.Control.Feedback type="invalid">
                              {errorState.lot_id}
                            </Form.Control.Feedback>
                            {(() => {
                              const selected = selfRegistrationLots.find(
                                l => String(l.id) === String(formData.lot_id)
                              );
                              if (!selected) return null;

                              if (selected.plate_validate === true || selected.plate_validate === 1) {
                                return (
                                  <Form.Group className="mb-4">
                                    <Form.Label className="fw-italic text-danger">
                                      Selected Lot is assigned for "PlateValidate"
                                    </Form.Label>
                                  </Form.Group>
                                );
                              }
                              return null;
                            })()}
                          </Form.Group>
                        </>
                      )}
                      <hr />
                      <IdFieldSectionSetting
                        formData={formData}
                        errorState={errorState}
                        handleCheckBox={handleCheckBox}
                        handleChange={handleChange}
                        handleUpdateChange={(e) => handleRadioChange("id_validation", e)}
                      />
                      <hr />
                      <RegisterOnTerminalSectionInSetting
                        handleTerminalUpdateChange={(e) => handleRadioChange("register_on_terminal", e)}
                        formData={formData}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Button className="" onClick={handleCreateQuickPermitSetup} disabled={!isFormValid}>
                  {isUpdate ? "Update" : "Save"}
                </Button>

                <Col lg="12" className="p-0">
                  {navigationButtons("/manage-meter-config", "/parking-permit-setup", propertyId, "", "QuickPermit Setup", true)}
                </Col>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
      {showAlert && api_success_message && <CustomContentAlert delay={4000} message={api_success_message} className="toast-success" />}
      {showError && api_error_message && <CustomContentAlert delay={4000} message={api_error_message} className="toast-error" />}
    </div>
  );
};

export default QuickPermitSetup;
